import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Note: Heroicons imports removed - using text-based icons for now
// TODO: Install @heroicons/react package or use alternative icons

import { 
  Button, 
  Card,
  Badge,
  ExpenseForm,
  ConfirmationDialog
} from '../components';
import { useToast } from '../components/ui/Toast';
import { useApiClient } from '../hooks/useApiClient';
import { uploadDocument, getDownloadUrl } from '../services/documentApi';
// theme not used

// Types
interface ExpenseApproval {
  id: string;
  approver_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'delegated';
  comments?: string;
  approval_level: number;
  created_at: string;
  approved_at?: string;
  approver: {
    id: string;
    name: string;
    email: string;
  };
}

interface ExpenseCategory {
  id: string;
  name: string;
  code: string;
  requires_receipt: boolean;
  max_amount?: number;
}

interface Expense {
  id: string;
  amount: number;
  currency: string;
  description: string;
  expense_date: string;
  merchant_name?: string;
  business_purpose?: string;
  is_billable: boolean;
  project_code?: string;
  status: 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'rejected' | 'paid' | 'cancelled';
  submitted_at?: string;
  receipt_url?: string;
  has_receipt: boolean;
  created_at: string;
  updated_at: string;
  version: number;
  category: ExpenseCategory;
  user: {
    id: string;
    name: string;
    email: string;
  };
  approvals: ExpenseApproval[];
}

interface ExpenseFormData {
  category_id: string;
  amount: number;
  currency: string;
  description: string;
  expense_date: string;
  merchant_name?: string;
  business_purpose?: string;
  is_billable: boolean;
  project_code?: string;
  receipt_urls?: string[];
}

const ExpenseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const apiClient = useApiClient();
  // Track the latest request to avoid stale updates under React 18 StrictMode
  const latestRequestId = useRef(0);

  // State
  const [expense, setExpense] = useState<Expense | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  // Load expense details
  useEffect(() => {
    const loadExpense = async () => {
      if (!id) return;
      const requestId = ++latestRequestId.current;
      // fetch start
      setLoading(true);
      try {
        const response = await apiClient.get<any>(`/expenses/${id}`);
        if (!response) {
          setError('Expense not found');
          showToast('Expense not found', 'error');
          return;
        }
        if (latestRequestId.current !== requestId) return; // stale
        const exp: Expense = (response && typeof response === 'object' && 'data' in response)
          ? (response.data as Expense)
          : (response as Expense);
        setExpense(exp);
        setError(null);
      } catch (error: any) {
        if (latestRequestId.current !== requestId) return; // stale
        const status = error?.response?.status;
        if (status === 403) {
          setError('Access denied. You do not have permission to view this expense.');
        } else if (status === 404) {
          setError('Expense not found');
        } else {
          setError('Failed to load expense. Please try again.');
        }
        console.error('Error loading expense:', error);
      } finally {
        if (latestRequestId.current === requestId) {
          setLoading(false);
          // fetch end
        }
      }
    };

    loadExpense();
  }, [id, apiClient, showToast]);

  // Helper: refetch the expense to sync latest server state (e.g., after uploads)
  const refetchExpense = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get<any>(`/expenses/${id}`);
      const exp: Expense = (response && typeof response === 'object' && 'data' in response)
        ? (response.data as Expense)
        : (response as Expense);
      setExpense(exp);
    } catch (e) {
      console.error('[ExpenseDetail] refetch failed', e);
    }
  };

  // Load categories for editing
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await apiClient.get<ExpenseCategory[]>('/expenses/categories/');
        setCategories(response);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    if (isEditing) {
      loadCategories();
    }
  }, [isEditing, apiClient]);

  // Handle expense update
  const handleUpdate = async (formData: ExpenseFormData) => {
    if (!expense) return;

    try {
      setSubmitting(true);

      const response = await apiClient.put<Expense>(`/expenses/${expense.id}`, formData);
      const updatedExpense = response;
      setExpense(updatedExpense);
      setIsEditing(false);
      showToast('Expense updated successfully', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to update expense', 'error');
      console.error('Error updating expense:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle expense deletion
  const handleDelete = async () => {
    if (!expense) return;

    try {
      setSubmitting(true);

      await apiClient.delete(`/expenses/${expense.id}`);
      showToast('Expense deleted successfully', 'success');
      navigate('/expenses');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete expense', 'error');
      console.error('Error deleting expense:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle submit for approval
  const handleSubmitForApproval = async () => {
    if (!expense) return;

    try {
      setSubmitting(true);

      // Pre-submit validations
      const requiresReceipt = !!expense.category?.requires_receipt;
      const hasReceipt = !!(expense.has_receipt && expense.receipt_url);
      if (requiresReceipt && !hasReceipt) {
        showToast('Receipt is required for this category before submission.', 'error');
        return;
      }
      if (!(expense.amount > 0)) {
        showToast('Amount must be greater than 0 before submission.', 'error');
        return;
      }

      const response = await apiClient.post<Expense>(`/expenses/${expense.id}/submit`, {});
      const updatedExpense = response;
      setExpense(updatedExpense);
      // Close the submit confirmation dialog on success
      setShowSubmitConfirm(false);
      showToast('Expense submitted for approval', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to submit expense', 'error');
      console.error('Error submitting expense:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'draft': return 'draft';
      case 'submitted': return 'submitted';
      case 'pending_approval': return 'pending';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      case 'paid': return 'paid';
      case 'cancelled': return 'cancelled';
      default: return 'default';
    }
  };

  // Check if expense can be edited
  const canEdit = expense && ['draft', 'rejected'].includes(expense.status);
  const canDelete = expense && ['draft', 'rejected'].includes(expense.status);
  const canSubmit = expense && ['draft', 'rejected'].includes(expense.status);
  const requiresReceipt = !!expense?.category?.requires_receipt;
  const hasReceipt = !!(expense?.has_receipt && expense?.receipt_url);
  const submitAllowed = !!canSubmit && (!requiresReceipt || hasReceipt) && (Number((expense as any).amount ?? 0) > 0);
  const submitLabel = expense?.status === 'rejected' ? 'Resubmit' : 'Submit for Approval';

  // Prefer rendering the expense if we have it, even if a background refetch is ongoing
  if (loading && !expense) {
    // Skeleton loader for better perceived performance
    return (
      <div className="space-y-6" aria-busy>
        <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4">
          <div className="animate-pulse flex items-center justify-between">
            <div>
              <div className="h-6 w-48 bg-gray-200 rounded" />
              <div className="mt-2 h-4 w-72 bg-gray-100 rounded" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-28 bg-gray-200 rounded" />
              <div className="h-8 w-24 bg-gray-200 rounded" />
              <div className="h-8 w-32 bg-gray-200 rounded" />
              <div className="h-8 w-32 bg-gray-200 rounded" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 bg-white rounded-lg shadow animate-pulse">
              <div className="h-5 w-40 bg-gray-200 rounded mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 w-24 bg-gray-100 rounded mb-2" />
                    <div className="h-5 w-48 bg-gray-200 rounded" />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <div className="h-4 w-28 bg-gray-100 rounded mb-2" />
                  <div className="h-20 w-full bg-gray-200 rounded" />
                </div>
              </div>
            </div>
            <div className="p-6 bg-white rounded-lg shadow animate-pulse">
              <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
              <div className="h-10 w-48 bg-gray-200 rounded" />
            </div>
          </div>
          <div className="space-y-6">
            <div className="p-6 bg-white rounded-lg shadow animate-pulse">
              <div className="h-5 w-40 bg-gray-200 rounded mb-4" />
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-gray-300 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-gray-200 rounded mb-1" />
                    <div className="h-3 w-40 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 bg-white rounded-lg shadow animate-pulse">
              <div className="h-5 w-48 bg-gray-200 rounded mb-4" />
              {[...Array(3)].map((_, i) => (
                <div key={i} className="mb-3">
                  <div className="h-4 w-24 bg-gray-100 rounded mb-1" />
                  <div className="h-4 w-32 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">Expense</h1>
            <Button variant="outline" onClick={() => navigate('/expenses')} aria-label="Back to expenses list">← Back</Button>
          </div>
        </div>
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <span className="text-red-600">⚠</span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{error || 'Expense not found'}</h2>
              <p className="text-sm text-gray-600 mb-4">We couldn’t load this expense. This could be due to missing permissions, an invalid link, or a temporary server issue.</p>
              <div className="flex items-center gap-2">
                <Button onClick={() => {
                  setLoading(true);
                  setError(null);
                  // retry load
                  (async () => {
                    try {
                      const res = await apiClient.get<any>(`/expenses/${id}`);
                      if (res) {
                        const exp: Expense = (res && typeof res === 'object' && 'data' in res)
                          ? (res.data as Expense)
                          : (res as Expense);
                        setExpense(exp);
                      }
                    } catch (e) {
                      setError('Failed to load expense. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  })();
                }}>Retry</Button>
                <Button variant="outline" onClick={() => navigate('/expenses')}>Go to Expenses</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Expense #{(expense.id || '').slice(-8)}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {expense.currency} {Number((expense as any).amount ?? 0).toFixed(2)} • {expense.category?.name || 'Uncategorized'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={getStatusVariant(expense.status)} size="lg" aria-label={`Status: ${expense.status.replace('_', ' ')}`}>
              {expense.status.replace('_', ' ').toUpperCase()}
            </Badge>

            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center gap-2"
                aria-label={isEditing ? 'Cancel edit' : 'Edit expense'}
              >
                ✏️
                {isEditing ? 'Cancel Edit' : 'Edit'}
              </Button>
            )}

            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2"
                aria-label="Delete expense"
              >
                🗑️
                Delete
              </Button>
            )}

            {canSubmit && (
              <Button
                size="sm"
                onClick={() => setShowSubmitConfirm(true)}
                disabled={!submitAllowed || submitting}
                className="flex items-center gap-2"
                aria-label="Submit expense for approval"
                title={!submitAllowed ? (requiresReceipt && !hasReceipt ? 'Receipt required before submission' : 'Amount must be greater than 0') : undefined}
              >
                ✓
                {submitLabel}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => navigate('/expenses')}
              className="flex items-center gap-2"
              aria-label="Back to expenses list"
            >
              ←
              Back to Expenses
            </Button>
          </div>
        </div>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Expense Details */}
            {isEditing ? (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Edit Expense
                </h2>
                <ExpenseForm
                  initialData={{
                    category_id: expense.category.id,
                    amount: expense.amount,
                    currency: expense.currency,
                    description: expense.description,
                    expense_date: expense.expense_date,
                    merchant_name: expense.merchant_name || '',
                    business_purpose: expense.business_purpose || '',
                    is_billable: expense.is_billable,
                    project_code: expense.project_code || '',
                    receipt_urls: expense.receipt_url ? [expense.receipt_url] : [],
                  }}
                  categories={categories}
                  loading={false}
                  submitting={submitting}
                  onSubmit={handleUpdate}
                  submitButtonText="Update Expense"
                  showSaveAsDraft={false}
                  showSubmitForApproval={false}
                  requiresReceipt={!!expense.category?.requires_receipt}
                  onUploadReceiptFiles={async (files) => {
                    if (!expense) return [];
                    // Upload files and return downloadable URLs
                    const urls: string[] = [];
                    for (const file of files) {
                      const resp = await uploadDocument(
                        apiClient as any,
                        file,
                        expense.id,
                        undefined,
                        { skipProcessing: true, timeoutMs: 45000 }
                      );
                      if (resp?.presigned_url) {
                        urls.push(resp.presigned_url);
                      } else if (resp?.document_id) {
                        try {
                          const dl = await getDownloadUrl(apiClient as any, resp.document_id);
                          if (dl?.url) urls.push(dl.url);
                        } catch (e) {
                          // ignore per-file failures, continue
                        }
                      }
                    }
                    // Optimistically update receipt presence to clear pending UI immediately
                    if (urls.length) {
                      setExpense((prev) => prev ? { ...prev, has_receipt: true, receipt_url: prev.receipt_url || urls[0] } as Expense : prev);
                      showToast('Receipt uploaded', 'success');
                      // Sync with server in background to ensure flags/URL are correct
                      refetchExpense();
                    }
                    return urls;
                  }}
                />
              </Card>
            ) : (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Expense Details
                </h2>
                {/* Rejection banner */}
                {expense.status === 'rejected' && (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <p className="font-medium mb-1">This expense was rejected.</p>
                    {(() => {
                      const latestRej = (expense.approvals || []).filter(a => a.status === 'rejected').sort((a,b)=> (new Date(b.approved_at||b.created_at).getTime()) - (new Date(a.approved_at||a.created_at).getTime()))[0];
                      return latestRej?.comments ? <p>Reason: {latestRej.comments}</p> : null;
                    })()}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <p className="text-lg font-semibold text-gray-900">
                      {expense.currency} {Number((expense as any).amount ?? 0).toFixed(2)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <p className="text-gray-900">{expense.category?.name || 'Uncategorized'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expense Date
                    </label>
                    <p className="text-gray-900">
                      {new Date(expense.expense_date).toLocaleDateString()}
                    </p>
                  </div>

                  {expense.merchant_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Merchant
                      </label>
                      <p className="text-gray-900">{expense.merchant_name}</p>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <p className="text-gray-900">{expense.description}</p>
                  </div>

                  {expense.business_purpose && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Purpose
                      </label>
                      <p className="text-gray-900">{expense.business_purpose}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billable
                    </label>
                    <Badge variant={expense.is_billable ? 'success' : 'secondary'}>
                      {expense.is_billable ? 'Yes' : 'No'}
                    </Badge>
                  </div>

                  {expense.project_code && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Code
                      </label>
                      <p className="text-gray-900">{expense.project_code}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Receipt */}
            {expense.has_receipt && expense.receipt_url && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Receipt
                </h2>
                <div className="flex items-center gap-4">
                  📄
                  <div>
                    <p className="font-medium text-gray-900">Receipt attached</p>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => window.open(expense.receipt_url, '_blank')}
                    >
                      View Receipt
                    </Button>
                  </div>
                </div>
              </Card>
            )}
            {/* Missing receipt hint when required */}
            {requiresReceipt && !hasReceipt && (
              <Card className="p-4 border-yellow-200 bg-yellow-50 text-yellow-900">
                <div className="text-sm">A receipt is required to submit this expense. Please attach a receipt.</div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Timeline */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Status Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created</p>
                    <p className="text-xs text-gray-500">
                      {new Date(expense.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {expense.submitted_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Submitted</p>
                      <p className="text-xs text-gray-500">
                        {new Date(expense.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {(expense.approvals || []).map((approval) => (
                  <div key={approval.id} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      approval.status === 'approved' ? 'bg-green-500' :
                      approval.status === 'rejected' ? 'bg-red-500' :
                      'bg-gray-400'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {approval.status === 'approved' ? 'Approved' :
                         approval.status === 'rejected' ? 'Rejected' :
                         'Pending Approval'} by {approval.approver?.name || 'Unknown'}
                      </p>
                      {approval.approved_at && (
                        <p className="text-xs text-gray-500">
                          {new Date(approval.approved_at).toLocaleString()}
                        </p>
                      )}
                      {approval.comments && (
                        <p className="text-xs text-gray-600 mt-1">
                          "{approval.comments}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Expense Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Expense Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Created by
                  </label>
                  <p className="text-sm text-gray-900">{expense.user?.name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{expense.user?.email || ''}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last updated
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(expense.updated_at).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Version
                  </label>
                  <p className="text-sm text-gray-900">v{expense.version}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Expense"
          description="Are you sure you want to delete this expense? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="destructive"
          loading={submitting}
        />

        {/* Submit Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showSubmitConfirm}
          onClose={() => setShowSubmitConfirm(false)}
          onConfirm={handleSubmitForApproval}
          title="Submit for Approval"
          description="Are you sure you want to submit this expense for approval? You won't be able to edit it after submission."
          confirmText="Submit"
          cancelText="Cancel"
          loading={submitting}
        />
    </div>
  );
};

export default ExpenseDetail;
